import { Controller } from "@hotwired/stimulus";

// Orchestrates the search page - handles load more pagination and auto-search on map move.
// Automatically triggers a new search when the map is significantly moved.
export default class extends Controller {
  static outlets = ["map"]; // Direct communication with map controller
  static values = {
    lat: Number,
    lng: Number,
  };

  connect() {
    this.handleMapChange = this.handleMapChange.bind(this);
    this.handleMapDragStart = this.handleMapDragStart.bind(this);
    this.searchDebounceTimer = null;
    this.isDragging = false;
    this.isLoadingMore = false;

    // Listen for map drag start (suppress search during active drag)
    document.addEventListener("map:dragstart", this.handleMapDragStart);
    // Listen for map region changes (pan, zoom)
    document.addEventListener("map:regionchangeend", this.handleMapChange);

    // Reset loading state when Turbo completes (button is replaced by stream)
    this.handleSubmitEnd = () => {
      this.isLoadingMore = false;
    };
    document.addEventListener("turbo:submit-end", this.handleSubmitEnd);

    // Sync the permanent map to the current search location
    this.syncMapCenter();
  }

  syncMapCenter() {
    const mapElement = document.getElementById("search-map");

    if (!mapElement) {
      // During Turbo page visits, permanent elements may not be in DOM yet
      this.handleSyncMapCenter = () => this.syncMapCenter();
      document.addEventListener("turbo:render", this.handleSyncMapCenter, {
        once: true,
      });
      return;
    }

    // Wait for map to be ready, then update center
    const updateCenter = () => {
      if (this.hasMapOutlet) {
        this.mapOutlet.setCenter({ lat: this.latValue, lng: this.lngValue });
      }
    };

    if (mapElement.dataset.mapReady) {
      updateCenter();
    } else {
      mapElement.addEventListener("map:ready", updateCenter, { once: true });
    }
  }

  disconnect() {
    document.removeEventListener("map:dragstart", this.handleMapDragStart);
    document.removeEventListener("map:regionchangeend", this.handleMapChange);
    document.removeEventListener("turbo:submit-end", this.handleSubmitEnd);
    if (this.handleSyncMapCenter) {
      document.removeEventListener("turbo:render", this.handleSyncMapCenter);
    }
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
  }

  handleMapDragStart() {
    this.isDragging = true;
    // Cancel any pending search - user is still exploring
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }
  }

  loadMore(event) {
    event.preventDefault();

    // Prevent double-triggers during loading
    if (this.isLoadingMore) return;

    // On mobile, don't auto-load when sheet is collapsed (map-focused)
    // Allow manual clicks but skip visibility-triggered loads
    const isAutoTrigger = event.type !== "click";
    const sheetState = this.element.dataset.mobileSheetStateValue;
    if (isAutoTrigger && sheetState === "collapsed") {
      return;
    }

    const page = event.params.page;
    const form = document.getElementById("search-form");

    if (!form) {
      console.error("Search form not found");
      return;
    }

    // Set loading state and update button UI
    this.isLoadingMore = true;
    const button = event.target.closest("button");
    if (button) {
      button.disabled = true;
      button.textContent = "Loading...";
    }

    // Add/update page param in form, then submit
    // Turbo Stream response will append results and replace the button
    let pageInput = form.querySelector('input[name="page"]');
    if (!pageInput) {
      pageInput = document.createElement("input");
      pageInput.type = "hidden";
      pageInput.name = "page";
      form.appendChild(pageInput);
    }
    pageInput.value = page;

    form.requestSubmit();
  }

  handleMapChange(event) {
    const { center, bounds } = event.detail;
    this.isDragging = false;

    // Scale threshold to ~20% of visible region, more permissive at high zoom
    const latSpan = bounds.north - bounds.south;
    const lngSpan = bounds.east - bounds.west;
    const viewportSpan = Math.max(latSpan, lngSpan);

    // Estimate zoom: ~0.02 span at zoom 14, ~0.5 at zoom 10
    // At street level (small span), increase threshold so small pans don't trigger
    const estimatedZoom = Math.log2(360 / viewportSpan);
    const zoomMultiplier = estimatedZoom > 13 ? 1.5 : 1.0; // Less sensitive at street level
    const threshold = viewportSpan * 0.2 * zoomMultiplier;

    const latDiff = Math.abs(center.lat - this.latValue);
    const lngDiff = Math.abs(center.lng - this.lngValue);

    if (latDiff < threshold && lngDiff < threshold) {
      return; // Not a significant change
    }

    // Update stored coordinates
    this.latValue = center.lat;
    this.lngValue = center.lng;

    // Debounce: wait for user to settle after dragging (longer delay)
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }

    this.searchDebounceTimer = setTimeout(() => {
      this.searchInArea();
    }, 500); // Longer delay to let user settle
  }

  searchInArea() {
    const form = document.getElementById("search-form");
    if (!form) return;

    // Update hidden lat/lng fields
    const latField = form.querySelector('[name="lat"]');
    const lngField = form.querySelector('[name="lng"]');

    if (latField) latField.value = this.latValue;
    if (lngField) lngField.value = this.lngValue;

    // Clear location name since we're now searching by map area
    const locationField = form.querySelector('[name="location"]');
    if (locationField) locationField.value = "";

    // Reset page to 1 for new search (page may have been set by Load More)
    const pageField = form.querySelector('[name="page"]');
    if (pageField) pageField.value = 1;

    // Clear expanded flag — user is expressing new geographic intent
    const expandedField = form.querySelector('[name="expanded"]');
    if (expandedField) {
      expandedField.remove();
      const url = new URL(window.location);
      url.searchParams.delete("expanded");
      history.replaceState(history.state, "", url);
    }

    // Update header search placeholder to indicate map-based search
    this.updateHeaderPlaceholder("Churches in Map Area");

    this.submitForm(form);
  }

  updateHeaderPlaceholder(text) {
    // Update the searchbox controller's inactive placeholder
    const searchbox = document.querySelector("[data-controller~='searchbox']");
    if (searchbox) {
      searchbox.dataset.searchboxPlaceholderInactiveValue = text;
      // Also update the input directly if not active
      const input = searchbox.querySelector("[data-searchbox-target='input']");
      if (input && !searchbox.querySelector(".active")) {
        input.placeholder = text;
      }
    }
  }

  changeSort(event) {
    const sortValue = event.target.value;
    const sortField = document.getElementById("search-sort-field");
    const form = document.getElementById("search-form");

    if (sortField) {
      sortField.value = sortValue;
    }

    if (form) {
      // Reset page to 1 for new search (page may have been set by Load More)
      const pageField = form.querySelector('[name="page"]');
      if (pageField) pageField.value = 1;

      this.submitForm(form);
    }
  }

  submitForm(form) {
    // Native submission - Turbo handles Accept header, response processing, and URL update
    // Form must have data-turbo-stream="true" and data-turbo-action="advance"
    form.requestSubmit();
  }
}
